import { useCallback, useDebugValue } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AsyncEntity } from '../../global';
import { RootState } from '../../store';
import { contentsSelectors } from './contents.state';
import { ContentCard, ContentCardLocale } from './contents.types';
import { AppDispatch } from 'src/index';
import { useUploadFileToBucket } from 'src/hooks/useUploadFileToBucket';
import { updateContent } from 'src/features/contents/contents.state';
import { uploadCardImage } from 'src/features/contents/contents.api';

export const useContentsState = (): RootState['contents'] => {
  const contents = useSelector<RootState, RootState['contents']>(state => state.contents);

  useDebugValue(contents);

  return contents;
};

export const useContentCards = (): ContentCard[] => {
  const contents = useSelector<RootState, RootState['contents']>(state => state.contents);

  const result = contentsSelectors.selectAll(contents);

  useDebugValue(result);

  return result;
};

export const useContentById = (id: string): AsyncEntity<ContentCard> | null => {
  const contents = useSelector<RootState, RootState['contents']>(state => state.contents);

  const result = contentsSelectors.selectById(contents, id) || null;

  useDebugValue(result);

  return result;
};
export const useContentLocalesById = (id: string): AsyncEntity<ContentCardLocale[]> => {
  const contents = useSelector<RootState, RootState['contents']>(state => state.contents);

  const result = contentsSelectors.selectById(contents, id) || null;

  useDebugValue(result);

  return result?.locales || [];
};

export type UseCardPoster = (params: { contentId: string }) => {
  progress: number;
  uploadContentImage: (file: File, imageType: 'ver' | 'hor') => void;
};

export const useContentImage: UseCardPoster = ({ contentId }) => {
  const dispatch: AppDispatch = useDispatch();
  const [progress, uploadFile] = useUploadFileToBucket();

  const uploadContentImage = useCallback(
    async (file: File, imageType: 'ver' | 'hor') => {
      const { name: filename } = file;

      const credentials = await uploadCardImage({
        contentId,
        data: { filename },
      });

      const { url, fields } = credentials.data;
      const { key } = fields;

      if (credentials.status < 400) {
        const response = await uploadFile({ ...credentials.data, file });
        const data = imageType === 'ver' ? { imageVer: `${url}/${key}` } : { image: `${url}/${key}` };
        if (response) {
          dispatch(
            updateContent({
              contentId,
              data: data,
            })
          );
        }
      }
    },
    [contentId, dispatch, uploadFile]
  );

  return { progress, uploadContentImage };
};
